import React from "react";
import { getAPIBasePath } from "../data/StripeData";
import { getUserIdToken } from "../utils/auth";
import { consola } from "consola";

/**
 * Hook for generating AI summaries of reports with caching support
 * @param {Object} options - Configuration options
 * @param {number} options.cacheExpiryHours - Cache expiry time in hours (default: 24)
 * @param {boolean} options.enableCache - Whether to enable caching (default: true)
 */
const useReportSummary = (options = {}) => {
  const {
    cacheExpiryHours = 24,
    enableCache = true,
  } = options;

  // Constants
  const CACHE_KEY_PREFIX = "report_summary_";

  // Cache utilities
  const getCacheKey = React.useCallback((report) => {
    // Create a unique cache key based on report content
    // We'll use a simple hash of the sanitized report data
    const sanitizedReport = {
      ...report,
      clientFirstName: undefined,
      clientLastName: undefined,
    };
    
    // Create a deterministic string from the report for hashing
    const reportString = JSON.stringify(sanitizedReport, Object.keys(sanitizedReport).sort());
    
    // Simple hash function for cache key
    let hash = 0;
    for (let i = 0; i < reportString.length; i++) {
      const char = reportString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return `${CACHE_KEY_PREFIX}${Math.abs(hash)}`;
  }, []);

  const getCachedSummary = React.useCallback((report) => {
    if (!enableCache) return null;
    
    try {
      const cacheKey = getCacheKey(report);
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const { summary, timestamp } = JSON.parse(cached);
        const isExpired = Date.now() - timestamp > cacheExpiryHours * 60 * 60 * 1000;
        
        if (!isExpired) {
          consola.debug("Report summary retrieved from cache:", cacheKey);
          return summary;
        } else {
          // Remove expired cache entry
          localStorage.removeItem(cacheKey);
          consola.debug("Expired cache entry removed:", cacheKey);
        }
      }
    } catch (error) {
      consola.warn("Error reading report summary cache:", error);
    }
    
    return null;
  }, [enableCache, cacheExpiryHours, getCacheKey]);

  const getPersistedInsightsFromReport = React.useCallback((report) => {
    const c = report?.aiInsightsCache;
    if (
      c &&
      typeof c.title === "string" &&
      c.title.trim() !== "" &&
      typeof c.summary === "string" &&
      c.summary.trim() !== ""
    ) {
      return { title: c.title, summary: c.summary };
    }
    return null;
  }, []);

  const setCachedSummary = React.useCallback((report, summary) => {
    if (!enableCache) return;
    
    try {
      const cacheKey = getCacheKey(report);
      const cacheData = {
        summary,
        timestamp: Date.now(),
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      consola.debug("Report summary cached:", cacheKey);
    } catch (error) {
      consola.warn("Error caching report summary:", error);
    }
  }, [enableCache, getCacheKey]);

  // Clear cache for a specific report
  const clearCacheForReport = React.useCallback((report) => {
    try {
      const cacheKey = getCacheKey(report);
      localStorage.removeItem(cacheKey);
      consola.debug("Cache cleared for report:", cacheKey);
    } catch (error) {
      consola.warn("Error clearing report summary cache:", error);
    }
  }, [getCacheKey]);

  // Clear all report summary caches
  const clearAllCache = React.useCallback(() => {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;
      
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
          clearedCount++;
        }
      });
      
      consola.debug(`Cleared ${clearedCount} report summary cache entries`);
      return clearedCount;
    } catch (error) {
      consola.warn("Error clearing all report summary caches:", error);
      return 0;
    }
  }, []);

  const getSummaryFromReport = React.useCallback(
    async (report, abortSignal, options = {}) => {
      const { forceRefresh = false } = options;
      if (!report) {
        consola.error(new Error("No report provided"));
        return null;
      }

      if (!forceRefresh) {
        const persisted = getPersistedInsightsFromReport(report);
        if (persisted) {
          return { ...persisted, source: "firestore" };
        }

        const cachedSummary = getCachedSummary(report);
        if (cachedSummary) {
          return { ...cachedSummary, source: "localStorage" };
        }
      }

      try {
        const path = getAPIBasePath();
        const fullPath = `${path}/report_summary`;

        // Strip out PII before sending to OpenAI
        const sanitizedReport = {
          ...report,
          clientFirstName: undefined,
          clientLastName: undefined,
        };

        consola.debug(
          "++++++ READ -> useReportSummary: getSummaryFromReport",
          JSON.stringify(sanitizedReport, null, 2),
        );

        const token = await getUserIdToken();
        const response = await fetch(fullPath, {
          method: "POST",
          body: JSON.stringify(sanitizedReport),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: abortSignal, // Add abort signal to the request
        });

        if (response.status === 200) {
          const result = await response.json();
          const summary = result.response || null;

          consola.debug(
            "++++++ READ <- useReportSummary: getSummaryFromReport",
            result,
          );

          // Cache the successful result
          if (summary) {
            setCachedSummary(report, summary);
            return { ...summary, source: "remote" };
          }

          return null;
        } else if (response.status === 403) {
          throw new Error(
            "AI report insights require a Pro plan. Upgrade to unlock.",
          );
        } else if (response.status === 429) {
          // Handle rate limiting
          const result = await response.json();
          consola.warn("OpenAI rate limited:", result);
          throw new Error(`Rate limited: ${result.error || "Please try again in a few minutes"}`);
        } else {
          // Handle other errors
          let errorMessage = "Error getting report summary";
          try {
            const errorResult = await response.json();
            errorMessage = errorResult.error || errorMessage;
          } catch (e) {
            // If we can't parse the error response, use the status text
            errorMessage = response.statusText || errorMessage;
          }
          consola.error(new Error(errorMessage));
          throw new Error(errorMessage);
        }
      } catch (error) {
        // Don't log error if the request was aborted
        if (error.name === "AbortError") {
          consola.debug("Report summary request was cancelled");
          return null;
        }
        consola.error("Error in getSummaryFromReport:", error);
        return null;
      }
    },
    [
      getCachedSummary,
      setCachedSummary,
      getPersistedInsightsFromReport,
    ],
  );

  return {
    getSummaryFromReport,
    clearCacheForReport,
    clearAllCache,
    // Utility to check if a report has cached summary
    hasCachedSummary: (report) => getCachedSummary(report) !== null,
  };
};

export default useReportSummary;
